import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Video } from 'modules/styled/web';
import { Box, InlineLink, View, Image, Button, Row, TextButton } from 'modules/styled';
import { Text as RText } from 'react-native';
import { fonts, GlobalStyle } from 'app/styles';
import { tween } from 'app/utils';
import * as navigationActions from 'modules/navigation/navigation.actions';
import CommunityList from 'modules/community/communityList.component';
import SocialIcons from 'modules/navigation/social.icons';
import { getCommunities } from 'modules/community/community.actions';
import Marquee from './post.marquee';
import AppStoreButtons from './appStoreButtons';
import { styled as dStyled } from 'dripsy';

if (process.env.BROWSER === true) {
  require('app/styles/fonts.css');
  require('react-toastify/dist/ReactToastify.css');
  require('react-smartbanner/dist/main.css');
}

const SHOW_FIXED_JOIN_HEIGHT = 250;
const SHOW_FIXED_LOGO = 500;
const VID_RATIO = 2.167042889390519;
const VID_TO_PHONE = 0.85;

const Section = ({ sx, children }) => (
  <View
    sx={{
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
      padding: 6,
      paddingLeft: 2,
      paddingRight: 2,
      maxWidth: 75 * 8,
      ...sx,
    }}
  >
    {children}
  </View>
);

const SplashText = dStyled(RText)({
  fontFamily: fonts.HELVETICA_NEUE,
  color: 'black',
  fontSize: [4, 5],
  lineHeight: [4, 5],
  marginTop: 2,
});

const SectionText = dStyled(RText)({
  fontFamily: fonts.HELVETICA_NEUE,
  flexDirection: 'column',
  fontSize: [2.001 * 8, 3.001 * 8],
  lineHeight: [2.001 * 8, 3.001 * 8],
});

class Splash extends Component {
  static propTypes = {
    history: PropTypes.object,
    actions: PropTypes.object,
    communities: PropTypes.object,
    screenSize: PropTypes.number,
  };

  state = {
    showJoinButton: false,
  };

  static fetchData(dispatch) {
    return dispatch(getCommunities());
  }

  onScroll = () => {
    const showJoinButton =
      document.body.scrollHeight - window.innerHeight - window.scrollY <
      SHOW_FIXED_JOIN_HEIGHT;
    if (this.state.showJoinButton !== showJoinButton) this.setState({ showJoinButton });

    const showFixedLogo = window.scrollY > SHOW_FIXED_LOGO;
    if (this.state.showFixedLogo !== showFixedLogo) this.setState({ showFixedLogo });
  };

  componentDidMount() {
    const { communities, actions } = this.props;
    window.addEventListener('scroll', this.onScroll);
    tween.start();
    if (!communities.length) actions.getCommunities();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    tween.stop();
  }

  signUp = async (modal) => {
    const { actions, history } = this.props;
    history.push('/communities');
    actions.showModal(modal);
  };

  render() {
    const { screenSize } = this.props;
    const { showFixedLogo, showJoinButton } = this.state;

    const sectionSpace = screenSize ? 4 : 8;

    return (
      <View
        sx={{
          bg: 'white',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <GlobalStyle />
        <Marquee />
        <Section sx={{ mt: [4, 0], minHeight: '100vh' }}>
          <InlineLink to={'/relevant/top'}>
            <Image
              sx={{
                height: [7 * 8, 10 * 8],
                width: [7 * 8 * 5, 10 * 8 * 5],
                maxWidth: '100%',
              }}
              resizeMode="contain"
              source={require('public/img/logo.png')}
              alt={'Relevant'}
            />
          </InlineLink>
          <Box sx={{ mt: [6, 12] }} />
          <SplashText style={{ width: 'calc(100vw - 32px)' }}>
            Resist algorithmic <span style={{ whiteSpace: 'nowrap' }}>oppression.</span>
          </SplashText>

          <SplashText sx={{ mt: 6 }} style={{ width: 'calc(100vw - 32px)' }}>
            Get{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              Relevant{' '}
              <span role="img" aria-label="emoji">
                🤘
              </span>
            </span>
          </SplashText>

          <View
            sx={{
              mt: [12 * 8, 8],
              flexDirection: ['column', 'row'],
              alignItems: 'center',
            }}
          >
            <View
              webContainerSx={{ alignSelf: ['stretch', 'flex-start'] }}
              sx={{ alignSelf: ['stretch', 'flex-start'] }}
            >
              <Button
                sx={{
                  height: 8,
                  px: 6,
                  py: 2,
                  mr: [0, 2],
                  flex: 1,
                }}
                onPress={() => this.signUp('signupSocial')}
              >
                <SectionText sx={{ color: 'white' }}>Join Now</SectionText>
              </Button>
            </View>
            <Box sx={{ mt: [2, 0], mr: [0, 2] }}>
              <SectionText>or</SectionText>
            </Box>
            <Box sx={{ mt: [2, 0] }}>
              <TextButton onPress={() => this.signUp('login')}>
                <SectionText sx={{ textDecorationLine: 'underline', color: 'blue' }}>
                  Log In
                </SectionText>
              </TextButton>
            </Box>
          </View>
        </Section>

        <Section mt={sectionSpace}>
          <SplashText sx={{ fontWeight: 'bold' }}>No Likes.</SplashText>
          <SplashText sx={{ mt: 6 }}>
            On Relevant posts are ranked according to quality, not popularity. This means
            only relevant content reaches the top of the feed.
          </SplashText>
        </Section>

        <Section mt={sectionSpace}>
          <SplashText sx={{ fontWeight: 'bold' }}>No Followers.</SplashText>
          <SplashText sx={{ mt: 6 }}>
            Relevant is a community-centric platform. You don’t need thousands of
            followers to be heard.
          </SplashText>
        </Section>

        <Section mt={sectionSpace}>
          <SplashText sx={{ fontWeight: 'bold' }}>No algorithms.</SplashText>
          <SplashText sx={{ mt: 6 }}>
            Forget filter bubbles, on Relevant everyone sees the same information.
            Communities decide what is relevant to them.
          </SplashText>
        </Section>

        <Section mt={sectionSpace}>
          <SplashText style={{ fontWeight: 'bold' }}>Find your community...</SplashText>
          <View sx={{ mt: 4 }} />
          <CommunityList p={0} hashtags />
        </Section>

        <View
          sx={{
            margin: 'auto',
            flex: [null, 1],
            mt: sectionSpace,
            flexDirection: ['column', 'revert'],
            justifyContent: 'center',
            alignItems: 'center',
            height: ['100vh', 'auto'],
            mb: 0,
          }}
        >
          <View
            webContainerSx={{ zIndex: 10 }}
            sx={{
              flex: ['auto', 1],
              flexDirection: 'column',
              align: ['center', 'flex-start'],
              justifyContent: 'center',
              zIndex: 10,
              overflow: 'hidden',
            }}
          >
            <Row sx={{ alignItems: 'center' }}>
              <Image
                sx={{
                  height: [0, 8 * 8],
                  width: [0, 8 * 8 * 5],
                  mt: 'auto',
                  zIndex: 1,
                }}
                resizeMode="contain"
                source={require('public/img/logo.png')}
                alt={'Relevant'}
              />
            </Row>

            <AppStoreButtons sx={{ mt: [4, 8], mb: [1, 0] }} />

            <View
              sx={{
                top: ['auto', 4],
                right: ['auto', 1],
                alignSelf: 'center',
                position: ['relative', 'fixed'],
                justifyContent: 'center',
              }}
            >
              <SocialIcons />
            </View>
          </View>
          <View
            sx={{
              position: ['absolute', 'relative'],
              bottom: [0, 'auto'],
              right: ['-35vw', 'auto'],
              opacity: [0.5, 1],
              transform: 'rotate(-2.82deg)',
              ml: 3,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Video
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate3d(-50%, -50%, 0)',
                width: ['auto', VID_TO_PHONE * 50 * 8],
                height: [`${VID_TO_PHONE * 100}vh`, VID_TO_PHONE * 50 * VID_RATIO * 8],
              }}
              autoPlay
              loop
              muted
              playsInline
            >
              <source src="/img/vid.webm" type="video/webm" />
              <source src="/img/vid.mp4" type="video/mp4" />
            </Video>
            <Image
              sx={{
                width: [60 * 8, 60 * 8],
                height: ['100vh', VID_TO_PHONE * 50 * VID_RATIO * 8 * 2],
              }}
              resizeMode="contain"
              source={require('public/img/phone-blank.png')}
              alt={'Relevant Phone'}
            />
          </View>
        </View>

        <View
          sx={{
            mt: [1, 2],
            position: 'fixed',
            left: 3,
            top: 4,
            opacity: showFixedLogo && !screenSize ? 1 : 0,
            transition: 'opacity 0.1s linear',
          }}
        >
          <InlineLink to={'/relevant/top'}>
            <Image
              sx={{ height: [5, 7], width: [5, 7] }}
              resizeMode="contain"
              source={require('public/img/r-big.png')}
              alt={'Relevant'}
            />
          </InlineLink>
        </View>

        <View
          sx={{
            position: 'fixed',
            left: '50%',
            transform: `translate3d(-50%, ${showJoinButton ? '0px' : '200px'}, 0)`,
            transition: 'transform 0.3s ease-in',
            bottom: [5, 8],
            zIndex: 1000,
            width: 'auto',
            mb: 0,
            px: [2, 0],
          }}
        >
          <Button
            sx={{ flex: 1, height: 8, px: 6, py: 2 }}
            onPress={() => this.signUp('signupSocial')}
          >
            <SectionText sx={{ color: 'white' }}>Join Relevant</SectionText>
          </Button>
        </View>
      </View>
    );
  }
}

function mapStateToProps(state) {
  return {
    screenSize: state.navigation.screenSize,
    communities: state.community.communities,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...navigationActions,
        getCommunities,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Splash);
